import * as THREE from 'three';
import type { NodeImplementation } from '@spotar/runtime';
import { applyEaseCurve } from '../helper/easing-helper';
import { runFrameAnimation, stopFrameAnimation } from '../helper/frame-animation-helper';
import { sceneHelper } from '../helper/scene-helper';
import type { Object3D } from 'three';

const ANGLE_EPSILON_RADIANS = 1e-6;

type Vector3Deg = { x: number; y: number; z: number } | [number, number, number];

type RotateToData = {
  target: { id: string };
  rotation: Vector3Deg;
  speed?: number;
  curve?: string;
};

function rotationDegreesToEuler(rotation: Vector3Deg, order: THREE.EulerOrder): THREE.Euler {
  if (Array.isArray(rotation)) {
    return new THREE.Euler(
      THREE.MathUtils.degToRad(rotation[0]),
      THREE.MathUtils.degToRad(rotation[1]),
      THREE.MathUtils.degToRad(rotation[2]),
      order
    );
  }
  return new THREE.Euler(
    THREE.MathUtils.degToRad(rotation.x),
    THREE.MathUtils.degToRad(rotation.y),
    THREE.MathUtils.degToRad(rotation.z),
    order
  );
}

function getRotationAnimationKey(object: Object3D): string {
  return `rotation:${object.uuid}`;
}

function animateObjectRotation(
  object: Object3D,
  targetEuler: THREE.Euler,
  speedDegreesPerSecond: number,
  curve: string | undefined
): Promise<boolean> {
  const animationKey = getRotationAnimationKey(object);
  const startQuat = object.quaternion.clone();
  const endQuat = new THREE.Quaternion().setFromEuler(targetEuler);
  const angleRadians = startQuat.angleTo(endQuat);

  if (angleRadians < ANGLE_EPSILON_RADIANS) {
    stopFrameAnimation(animationKey);
    object.rotation.copy(targetEuler);
    return Promise.resolve(true);
  }

  const speedRadiansPerSecond = THREE.MathUtils.degToRad(speedDegreesPerSecond);

  return runFrameAnimation({
    key: animationKey,
    durationSeconds: angleRadians / speedRadiansPerSecond,
    onProgress: (progress) =>
      object.quaternion.slerpQuaternions(startQuat, endQuat, applyEaseCurve(progress, curve)),
    onComplete: () => object.rotation.copy(targetEuler)
  });
}

export const runRotateToImplementation: NodeImplementation = async (data, _nodeId) => {
  const { target, rotation, speed, curve } = data as RotateToData;
  const object = sceneHelper?.getObjectByUUID(target.id);
  if (!object) {
    console.error(`Object ${target.id} not found`);
    return;
  }

  const targetEuler = rotationDegreesToEuler(rotation, object.rotation.order);
  const speedDegreesPerSecond = speed ?? 90;

  await animateObjectRotation(object, targetEuler, speedDegreesPerSecond, curve);
};
